import {getFbc} from 'FacebookPixelProvider'
import {LoginData} from 'attendee/Auth/Interfaces/login'
import {
  PasswordRecoverData,
  PasswordRecoverUpdateData,
} from 'attendee/Auth/Interfaces/password_recover'
import {RegisterData} from 'attendee/Auth/Interfaces/register'
import {UpdatePasswordData} from 'attendee/Auth/Interfaces/update_password'
import {UpdateProfileData} from 'attendee/Auth/Interfaces/update_profile'
import {attendeeAccessTokenkey, attendeeApiClient} from 'attendee/apiClient'
import {Attendee} from 'attendee/models/attendee'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {FullPageLoader} from 'organization/theme/FullPageLoader'

export interface AuthToken {
  access_token: string
  token_type: string
  expires_in: number
}

export type AuthRequiresVerification = {
  requiresVerification: true
  email: string
}

export type AuthResult = true | false | AuthRequiresVerification

export interface AuthContextProps {
  user: Attendee | null
  login: (data: LoginData) => Promise<AuthResult>
  register: (data: RegisterData) => Promise<AuthResult>
  updateProfile: (data: UpdateProfileData) => Promise<string | boolean>
  updatePassword: (data: UpdatePasswordData) => Promise<string | boolean>
  passwordRecover: (data: PasswordRecoverData) => Promise<string | boolean>
  passwordReset: (
    data: PasswordRecoverUpdateData,
    token: string,
    email: string,
  ) => Promise<string | boolean>
  logout: () => Promise<string | boolean>
  googleAuth: () => void
  googleCallback: (callback: string) => Promise<string | boolean>
  oneTabGoogleLogin: (response: any) => void
  updateCompany: (data: UpdateAttendeeCompanyData) => Promise<string | boolean>
  hasCompany: () => boolean
  saveAccessToken: (token: string) => void
  refreshUser: () => Promise<boolean>
  resendVerificationEmail: (email: string) => Promise<boolean>
}

export const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined,
)

export default function AuthProvider(props: {children: React.ReactElement}) {
  const apiClient = attendeeApiClient()
  const [user, setUser] = useState<Attendee | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveToken = (token: AuthToken) => {
    localStorage.setItem(attendeeAccessTokenkey, token.access_token)
  }

  const get = async (): Promise<boolean> => {
    try {
      const result = await apiClient.get('api/user').catch(() => false)

      if (result) {
        setUser(result)
        return true
      }
      return false
    } finally {
      setLoading(false)
    }
  }

  const register = async (data: RegisterData): Promise<AuthResult> => {
    try {
      const response: any = await apiClient.post(
        'api/register?fbc=' + getFbc(),
        data,
      )

      // Manual signup path: backend responds with a verification payload
      // instead of a token until the user clicks the email verification link.
      if (response?.requires_verification) {
        return {requiresVerification: true, email: response.email}
      }

      saveToken(response as AuthToken)
      await get()
      return true
    } catch (error: any) {
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  const login = async (data: LoginData): Promise<AuthResult> => {
    try {
      const response: any = await apiClient.post(
        'api/login?fbc=' + getFbc(),
        data,
      )
      saveToken(response as AuthToken)
      await get()
      return true
    } catch (error: any) {
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.code === 'email_not_verified'
      ) {
        return {
          requiresVerification: true,
          email: error.response.data.email,
        }
      }
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  const resendVerificationEmail = async (email: string) => {
    try {
      await apiClient.post('api/email/verification-notification', {email})
      return true
    } catch (error: any) {
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  const saveAccessToken = (token: string) => {
    localStorage.setItem(attendeeAccessTokenkey, token)
  }

  const updateProfile = async (data: UpdateProfileData) => {
    const result = await apiClient
      .post('api/update-profile', data)
      .then(setUser)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const updatePassword = async (data: UpdatePasswordData) => {
    const result = await apiClient
      .post('api/update-password', data)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const passwordRecover = async (data: PasswordRecoverData) => {
    const result = await apiClient
      .post('api/password-recover', data)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const logout = async () => {
    try {
      await apiClient.post('api/logout')
      setUser(null)
      window.location.reload()
      return true
    } catch (error: any) {
      toast.warning(error.message)
      return error.message
    }
  }

  const passwordReset = async (
    data: PasswordRecoverUpdateData,
    token: string,
    email: string,
  ) => {
    const result = await apiClient
      .post('api/password-reset', {
        ...data,
        token: token,
        email: email,
      })
      .then(saveToken)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const googleAuth = async () => {
    const result = await apiClient
      .get('api/google/auth?fbc=' + getFbc())
      .then((data) => {
        window.location.href = data.url

        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const googleCallback = async (callback: string) => {
    const result = await apiClient
      .get('api/google/callback' + callback + '&fbc=' + getFbc())
      .then(saveToken)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const oneTabGoogleLogin = async (data: {credential: string}) => {
    apiClient
      .get('api/google/one-tab/' + data.credential + '?fbc=' + getFbc())
      .then(saveToken)
      .then(get)
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
  }

  const updateCompany = async (data: UpdateAttendeeCompanyData) => {
    const result = await apiClient
      .post(`api/update-company`, data)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const hasCompany = (): boolean => {
    return (
      !!user?.eik &&
      user?.eik.trim() !== '' &&
      !!user?.company_name &&
      user?.company_name.trim() !== '' &&
      !!user?.mol &&
      user?.mol.trim() !== '' &&
      !!user?.country &&
      user?.country.trim() !== '' &&
      !!user?.city &&
      user?.city.trim() !== '' &&
      !!user?.postal_code &&
      user?.postal_code.trim() !== '' &&
      !!user?.address_line &&
      user?.address_line.trim() !== ''
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateProfile,
        updatePassword,
        passwordRecover,
        passwordReset,
        googleAuth,
        googleCallback,
        oneTabGoogleLogin,
        updateCompany,
        hasCompany,
        saveAccessToken,
        refreshUser: get,
        resendVerificationEmail,
      }}
    >
      {loading ? <FullPageLoader /> : props.children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export type UpdateAttendeeCompanyData = Pick<
  Attendee,
  | 'company_name'
  | 'eik'
  | 'vat_number'
  | 'mol'
  | 'country'
  | 'city'
  | 'postal_code'
  | 'address_line'
>
